import {PropsWithChildren, useCallback, useMemo, useRef, useState} from 'react';
import {NavigateOptions, useNavigate} from 'react-router-dom';
import {goToExternalLink} from 'utils/common';
import {DiscardChangesDialog} from '@chekin/shared';
import {
  DiscardCopy,
  NavigationGuardConfig,
  NavigationGuardContext,
  NavigationGuardContextValue,
  NavigationTarget,
} from './NavigationGuardContext';

const isExternal = (to: NavigationTarget): to is string =>
  typeof to === 'string' && /^https?:\/\//.test(to);

function NavigationGuardProvider({children}: PropsWithChildren) {
  const navigate = useNavigate();
  const guardsRef = useRef(new Map<string, NavigationGuardConfig>());
  const pendingOptionsRef = useRef<NavigateOptions | undefined>();
  const blockingGuardIdRef = useRef<string | null>(null);
  const [isGuarding, setIsGuarding] = useState(false);
  const [pendingTarget, setPendingTarget] = useState<NavigationTarget | null>(null);
  const [activeCopy, setActiveCopy] = useState<DiscardCopy | undefined>();

  const refreshGuarding = useCallback(() => {
    setIsGuarding([...guardsRef.current.values()].some(guard => guard.when));
  }, []);

  const registerGuard = useCallback(
    (id: string, config: NavigationGuardConfig) => {
      guardsRef.current.set(id, config);
      refreshGuarding();
    },
    [refreshGuarding],
  );

  const unregisterGuard = useCallback(
    (id: string) => {
      guardsRef.current.delete(id);
      refreshGuarding();
    },
    [refreshGuarding],
  );

  const performNavigate = useCallback(
    (to: NavigationTarget, options?: NavigateOptions) => {
      if (isExternal(to)) {
        goToExternalLink(to);
        return;
      }
      if (typeof to === 'number') {
        navigate(to);
        return;
      }
      navigate(to, options);
    },
    [navigate],
  );

  const guardedNavigate = useCallback(
    (to: NavigationTarget, options?: NavigateOptions) => {
      const blocking = [...guardsRef.current.entries()].find(([, guard]) => guard.when);
      if (!blocking) {
        performNavigate(to, options);
        return;
      }
      const [blockingId, blockingGuard] = blocking;
      const {when: _when, onDiscard: _onDiscard, ...copy} = blockingGuard;
      blockingGuardIdRef.current = blockingId;
      pendingOptionsRef.current = options;
      setActiveCopy(copy);
      setPendingTarget(to);
    },
    [performNavigate],
  );

  const closeDialog = useCallback(() => {
    blockingGuardIdRef.current = null;
    pendingOptionsRef.current = undefined;
    setPendingTarget(null);
    setActiveCopy(undefined);
  }, []);

  const confirmDiscard = useCallback(() => {
    const blockingId = blockingGuardIdRef.current;
    const blockingGuard = blockingId ? guardsRef.current.get(blockingId) : undefined;
    blockingGuard?.onDiscard?.();
    const target = pendingTarget;
    const options = pendingOptionsRef.current;
    closeDialog();
    if (target !== null) {
      performNavigate(target, options);
    }
  }, [pendingTarget, closeDialog, performNavigate]);

  const value = useMemo<NavigationGuardContextValue>(
    () => ({registerGuard, unregisterGuard, guardedNavigate, isGuarding}),
    [registerGuard, unregisterGuard, guardedNavigate, isGuarding],
  );

  return (
    <NavigationGuardContext.Provider value={value}>
      {children}
      <DiscardChangesDialog
        open={pendingTarget !== null}
        copy={activeCopy}
        onConfirm={confirmDiscard}
        onCancel={closeDialog}
      />
    </NavigationGuardContext.Provider>
  );
}

export {NavigationGuardProvider};
